import { statusStyle } from '../../utils/reportFormat';

export type PlanStatus = 'idea' | 'picked' | 'in_progress' | 'on_hold' | 'live' | 'dropped';

export const PLAN_STATUS_LABEL: Record<PlanStatus, string> = {
  idea: 'Idea',
  picked: 'Picked',
  in_progress: 'In progress',
  on_hold: 'On hold',
  live: 'Live',
  dropped: 'Dropped',
};

/** The Jira word each plan status stands for, so the one status colour (statusStyle) paints it. */
const COLOUR_WORD: Record<PlanStatus, string> = {
  idea: 'Ideation',
  picked: 'To Do',
  in_progress: 'In Progress',
  on_hold: 'On Hold',
  live: 'Released',
  dropped: 'Dropped',
};

export const planStatusStyle = (status: PlanStatus) => statusStyle(COLOUR_WORD[status]);

export const PRIORITIES = ['P0', 'P1', 'P2', 'P3'] as const;
export type Priority = (typeof PRIORITIES)[number];

export interface PlanNote {
  priority: Priority | null;
  dev_start: string | null;
  dev_end: string | null;
  progress_status: string | null;
  implementation_comments: string | null;
  pm_comments: string | null;
  planned_completion: string | null;
  prod_release_date: string | null;
  meeting_notes: string | null;
  project_team: string | null;
  rank: number | null;
  show_in_report: boolean;
  report_remark: string | null;
}

/** The sheet's text columns, in the sheet's order. */
export const NOTE_FIELDS: Array<{ key: keyof PlanNote; label: string; long?: boolean }> = [
  { key: 'planned_completion', label: 'Planned completion' },
  { key: 'dev_start', label: 'Development start' },
  { key: 'dev_end', label: 'Development end' },
  { key: 'prod_release_date', label: 'Prod release date' },
  { key: 'progress_status', label: 'Progress status', long: true },
  { key: 'implementation_comments', label: 'Implementation comments', long: true },
  { key: 'pm_comments', label: 'PM comments', long: true },
  { key: 'meeting_notes', label: 'Meeting comments', long: true },
  { key: 'project_team', label: 'Project team' },
];

export interface PlanRow {
  id: string;
  kind: 'epic' | 'roadmap' | 'idea' | 'ticket';
  issue_key: string | null;
  idea_id: number | null;
  project_key: string | null;
  project_name: string | null;
  component: string | null;
  title: string;
  jira_status: string | null;
  plan_status: PlanStatus;
  sheet_status: string | null;
  owner: string | null;
  progress: { tickets: number; shipped: number; ready: number; open: number; invalid: number; done_or_ready_pct: number | null } | null;
  roadmap: Array<{ key: string; title: string; status: string | null }>;
  note: PlanNote;
  requested_by: string | null;
  created_before_boundary: boolean;
  created_at: string | null;
  updated_at: string | null;
  not_in_mirror: boolean;
  jira_url: string | null;
}

export interface PlanGroup {
  project_key: string | null;
  project_name: string;
  rows: PlanRow[];
}

export interface PlanData {
  quarter: { id: string; label: string };
  before_boundary: boolean;
  tech_from: string;
  projects: PlanGroup[];
  adhoc: PlanRow[];
  ideation: PlanRow[];
  before: PlanRow[];
  can_edit: { all: boolean; projects: string[] };
  project_options: Array<{ project_key: string; project_name: string }>;
}

export interface ImportSummary {
  rows: number;
  keyed: number;
  notes_filled: number;
  new_ideas: number;
  already_known: number;
  keys_not_in_mirror: string[];
  unmatched_projects: string[];
  disagreements: Array<{ key: string; sheet: string; jira: string }>;
  warnings: string[];
}

export interface ImportRecord {
  id: number;
  filename: string | null;
  quarter: string;
  imported_by: string | null;
  imported_at: string;
  summary: ImportSummary;
}

export interface GoalRef {
  key: string;
  title: string;
  project_key: string | null;
  project_name: string | null;
  plan_status: PlanStatus;
}

export interface SprintColumn {
  label: string;
  number: number | null;
  ids: number[];
  start: string | null;
  end: string | null;
  current: boolean;
}

export interface RoadmapData {
  quarter: { id: string; label: string };
  sprints: SprintColumn[];
  goals: Array<GoalRef & { cells: Record<string, { tickets: number; done: number }> }>;
}

export type FunctionKey = 'Developers' | 'QA' | 'DevOps' | 'Product' | 'Design' | 'Unassigned';

export interface FunctionsData {
  quarter: { id: string; label: string };
  goal_count: number;
  functions: Array<{ key: FunctionKey; open: number; done: number; goals: number; sentence: string }>;
  goals: Array<GoalRef & { cells: Partial<Record<FunctionKey, { open: number; done: number }>> }>;
}

export type IntakeStage = 'waiting' | 'picked' | 'on_hold' | 'dropped';

export interface IntakeData {
  period: { key: string; label: string };
  summary: { came_in: number; stages: Record<IntakeStage, number>; carried_in: number };
  headline: string;
  items: Array<{
    key: string;
    title: string;
    project_key: string;
    project_name: string;
    status: string | null;
    stage: IntakeStage;
    reporter: string | null;
    age_days: number;
    created_at: string;
    carried: boolean;
    jira_url: string;
  }>;
}

export const INTAKE_STAGE_LABEL: Record<IntakeStage, string> = { waiting: 'Waiting', picked: 'Picked up', on_hold: 'On hold', dropped: 'Dropped' };
const INTAKE_COLOUR_WORD: Record<IntakeStage, string> = { waiting: 'To Do', picked: 'In Progress', on_hold: 'On Hold', dropped: 'Dropped' };
export const intakeStageStyle = (stage: IntakeStage) => statusStyle(INTAKE_COLOUR_WORD[stage]);
